import { p as parser, b as codedDastError, j as toXml } from "./create-error-node-BBWw3OO7.js";
import { k } from "./create-error-node-BBWw3OO7.js";
import { l as lezerToDast, v as visit, u as unified } from "./index-DtUZJWyj.js";
import { C, E, S, r, s, a } from "./index-DtUZJWyj.js";
import "@doenet/static-assets/entity-map";
function parse(inText) {
  return parser.parse(inText).cursor();
}
function showCursor(cursor) {
  return showNode(cursor.node);
}
function showNode(node) {
  let str = node.name;
  if (node.firstChild !== null) {
    str += "(" + showNode(node.firstChild) + ")";
  }
  if (node.nextSibling !== null) {
    str += "," + showNode(node.nextSibling);
  }
  return str;
}
function extractDastErrors(nodes) {
  if (Array.isArray(nodes)) {
    return nodes.flatMap(extractDastErrors);
  }
  if (nodes.type === "error") {
    return [nodes];
  }
  if (nodes.type === "root" || nodes.type === "element") {
    return extractDastErrors(nodes.children);
  }
  return [];
}
const isDastElement = (rootContent) => rootContent && typeof rootContent === "object" && rootContent.type === "element";
const isDastElementContent = (rootContent) => [
  "cdata",
  "comment",
  "element",
  "instruction",
  "text",
  "error",
  "macro",
  "function"
].includes(rootContent.type);
function detectVersionFromDoenetML(doenetML) {
  const parsed = lezerToDast(doenetML);
  const documentElement = parsed.children.find(
    (e) => e.type === "element" && e.name === "document"
  );
  if (!documentElement || !("xmlns" in documentElement.attributes)) {
    return null;
  }
  const xmlnsValue = documentElement.attributes["xmlns"];
  if (xmlnsValue.type !== "attribute" || xmlnsValue.children.length !== 1 || xmlnsValue.children[0].type !== "text") {
    return null;
  }
  const xmlnsTextNode = xmlnsValue.children[0];
  const xmlnsText = xmlnsValue.children[0].value;
  const match = xmlnsText.match(
    /^https:\/\/doenet\.org\/spec\/doenetml\/v([0-9a-zA-Z\.\-]+)$/
  );
  const documentTag = match ? match[0] : null;
  const versionString = match ? match[1] : null;
  if (match && documentTag && versionString != null) {
    const offsetStart = (match.index || 0) + documentTag.indexOf(versionString);
    const offsetEnd = offsetStart + versionString.length;
    return {
      version: versionString,
      position: {
        begin: xmlnsTextNode.position?.start.offset + offsetStart,
        end: xmlnsTextNode.position?.start.offset + offsetEnd
      }
    };
  }
  return null;
}
function readLangAttribute(element) {
  const attributes = element.attributes ?? {};
  const langName = Object.keys(attributes).find(
    (name) => name.toLowerCase() === "lang"
  );
  const langAttribute = langName === void 0 ? void 0 : attributes[langName];
  if (!langAttribute) {
    return void 0;
  }
  const [child, ...rest] = langAttribute.children;
  if (rest.length > 0 || child?.type !== "text") {
    return void 0;
  }
  return child.value.trim() || void 0;
}
function readDeclaredLangs(dast) {
  const langs = [];
  const seen = /* @__PURE__ */ new Set();
  function visit2(node) {
    if (node.type === "element" && node.name === "document") {
      const lang = readLangAttribute(node);
      if (lang !== void 0 && !seen.has(lang)) {
        seen.add(lang);
        langs.push(lang);
      }
    }
    for (const child of node.children) {
      if (child.type === "element") {
        visit2(child);
      }
    }
  }
  visit2(dast);
  return langs;
}
const NO_DECLARED_LANGS = [];
function declaredLangsInSource(source) {
  if (!/\blang\s*=/i.test(source)) {
    return NO_DECLARED_LANGS;
  }
  try {
    return readDeclaredLangs(lezerToDast(source));
  } catch {
    return NO_DECLARED_LANGS;
  }
}
const ELEMENT_EXPANSIONS = {
  chapter: {
    to: "division",
    attributes: {
      type: "chapter"
    }
  },
  part: {
    to: "division",
    attributes: {
      type: "part"
    }
  },
  section: {
    to: "division",
    attributes: {
      type: "section"
    }
  },
  subsection: {
    to: "division",
    attributes: {
      type: "subsection"
    }
  },
  subsubsection: {
    to: "division",
    attributes: {
      type: "subsubsection"
    }
  },
  paragraphs: {
    to: "division",
    attributes: {
      type: "paragraphs"
    }
  }
};
const pluginConvertPretextAttributes = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (!isDastElement(node)) {
        return;
      }
      if (node.attributes["xml:id"]) {
        const attr = node.attributes["xml:id"];
        attr.name = "name";
        delete node.attributes["xml:id"];
        node.attributes.name = attr;
      }
      if (node.name === "xref") {
        const refAttr = node.attributes.ref;
        if (!refAttr) {
          return;
        }
        const hasMacroChild = refAttr.children.some(
          (child) => child.type === "macro"
        );
        const hasNonWhitespaceTextChild = refAttr.children.some(
          (child) => child.type === "text" && child.value.trim() !== ""
        );
        if (hasMacroChild || !hasNonWhitespaceTextChild) {
          return;
        }
        const refText = refAttr.children.map((child) => child.type === "text" ? child.value : "").join("");
        refAttr.position;
        let macro = {
          type: "macro",
          attributes: {},
          path: [
            {
              type: "pathPart",
              index: [],
              name: refText
            }
          ]
        };
        refAttr.children.length = 0;
        refAttr.children.push(macro);
      }
    });
  };
};
function groupTextAndReferencesBySpacesOutsideParens({
  children,
  componentType,
  wrapSingleNonTextNodes = true
}) {
  for (const child of children) {
    if (!["text", "macro", "function"].includes(child.type)) {
      return { success: false };
    }
  }
  const newChildren = [];
  let currentGroup = [];
  let nParens = 0;
  for (const child of children) {
    switch (child.type) {
      case "macro":
      case "function":
        currentGroup.push(child);
        continue;
      case "text":
        const splitNodes = splitAtParensOrSpace(child);
        let currentText = "";
        let currentTextStartPos = splitNodes[0]?.position?.start;
        for (const node of splitNodes) {
          if (node.value === "(") {
            currentText += node.value;
            nParens++;
          } else if (node.value === ")") {
            if (nParens === 0) {
              return { success: false };
            } else {
              currentText += node.value;
              nParens--;
            }
          } else if (nParens === 0 && node.value.match(/\s/)) {
            if (currentText.length > 0) {
              currentGroup.push(
                createNewTextNode(
                  currentText,
                  currentTextStartPos,
                  child.source_doc
                )
              );
            }
            if (currentGroup.length > 0) {
              newChildren.push(
                createElementFromChildren(
                  componentType,
                  currentGroup,
                  wrapSingleNonTextNodes,
                  child.source_doc
                )
              );
              currentGroup = [];
            }
            currentText = "";
            currentTextStartPos = node.position?.end;
          } else {
            currentText += node.value;
          }
        }
        if (currentText.length > 0) {
          currentGroup.push(
            createNewTextNode(
              currentText,
              currentTextStartPos,
              child.source_doc
            )
          );
        }
        break;
      default:
        throw Error("Unreachable");
    }
  }
  if (nParens > 0) {
    return { success: false };
  }
  if (currentGroup.length > 0) {
    newChildren.push(
      createElementFromChildren(
        componentType,
        currentGroup,
        wrapSingleNonTextNodes
      )
    );
  }
  return { success: true, newChildren };
}
function createElementFromChildren(name, children, wrapSingleNonTextNodes, source_doc) {
  if (!wrapSingleNonTextNodes && children.length === 1 && children[0].type !== "text") {
    return children[0];
  }
  const newChild = {
    type: "element",
    name,
    children,
    attributes: {},
    source_doc
  };
  const startPos = children[0].position?.start;
  const endPos = children[children.length - 1].position?.end;
  if (startPos && endPos) {
    newChild.position = {
      start: startPos,
      end: endPos
    };
  }
  return newChild;
}
function createNewTextNode(text, startPos, source_doc) {
  const newNode = {
    type: "text",
    value: text,
    source_doc
  };
  if (startPos) {
    newNode.position = {
      start: startPos,
      end: computeEndPosition(text, startPos)
    };
  }
  return newNode;
}
function splitAtParensOrSpace(node) {
  let startPos = node.position.start;
  const splitText = [];
  for (const match of node.value.matchAll(/([()]|\s+|[^()\s]+)/g)) {
    const newNode = {
      type: "text",
      value: match[0],
      source_doc: node.source_doc
    };
    if (startPos) {
      newNode.position = {
        start: startPos,
        end: computeEndPosition(match[0], startPos)
      };
      startPos = newNode.position.end;
    }
    splitText.push(newNode);
  }
  return splitText;
}
function computeEndPosition(text, startPos) {
  const byLines = text.split("\n");
  const endPos = {
    line: startPos.line + byLines.length - 1,
    column: startPos.column + byLines[byLines.length - 1].length
  };
  if (startPos.offset != void 0) {
    endPos.offset = startPos.offset + text.length;
  }
  return endPos;
}
function repeatSugar(node) {
  if (node.name !== "repeat" && node.name !== "repeatForSequence") {
    throw Error(
      "Repeat sugar can only be applied to a `<repeat>` or a `<repeatForSequence>`"
    );
  }
  let setupChildren = [];
  if (node.attributes.valueName) {
    const attrChildren = node.attributes.valueName.children;
    if (attrChildren.length === 1 && attrChildren[0].type === "text") {
      setupChildren.push({
        type: "element",
        name: "_placeholder",
        children: [],
        attributes: {
          name: {
            type: "attribute",
            name: "name",
            children: [
              { type: "text", value: attrChildren[0].value }
            ]
          }
        },
        source_doc: node.source_doc
      });
    }
  }
  if (node.attributes.indexName) {
    const attrChildren = node.attributes.indexName.children;
    if (attrChildren.length === 1 && attrChildren[0].type === "text") {
      setupChildren.push({
        type: "element",
        name: "integer",
        children: [],
        attributes: {
          name: {
            type: "attribute",
            name: "name",
            children: [
              { type: "text", value: attrChildren[0].value }
            ]
          }
        },
        source_doc: node.source_doc
      });
    }
  }
  if (setupChildren.length > 0) {
    node.children.push({
      type: "element",
      name: "_repeatSetup",
      children: setupChildren,
      attributes: {},
      source_doc: node.source_doc
    });
  }
  if (node.name === "repeat") {
    let type = "math";
    if (node.attributes.type) {
      const attrChildren = node.attributes.type.children;
      if (attrChildren.length === 1 && attrChildren[0].type === "text") {
        type = attrChildren[0].value;
        if (!["math", "text", "number", "boolean"].includes(type)) {
          type = "math";
        }
      }
    }
    if (node.attributes.for) {
      const attrChildren = node.attributes.for.children;
      const groupResult = groupTextAndReferencesBySpacesOutsideParens({
        children: attrChildren,
        componentType: type,
        wrapSingleNonTextNodes: false
      });
      if (groupResult.success) {
        node.attributes.for.children = groupResult.newChildren;
      }
    }
  }
}
function conditionalContentSugar(node) {
  if (node.name !== "conditionalContent") {
    throw Error(
      "Conditional content sugar can only be applied to a `<conditionalContent>`"
    );
  }
  let nCaseChildren = 0;
  for (const child of node.children) {
    if (!isDastElement(child)) {
      continue;
    }
    if (child.name === "case") {
      nCaseChildren++;
    } else if (child.name === "else") {
      child.name = "case";
      nCaseChildren++;
    }
  }
  if (nCaseChildren === 0) {
    const attributes = {};
    node.children = [
      {
        type: "element",
        name: "case",
        children: node.children,
        attributes,
        source_doc: node.source_doc
      }
    ];
    if (node.attributes.condition) {
      attributes.condition = node.attributes.condition;
      delete node.attributes.condition;
    }
  }
  for (const child of node.children) {
    if (!isDastElement(child)) {
      continue;
    }
    if (child.name === "case") {
      child.children = [
        {
          type: "element",
          name: "group",
          children: child.children,
          attributes: {},
          source_doc: child.source_doc
        }
      ];
    }
  }
}
function selectSugar(node) {
  if (node.name !== "select") {
    throw Error("Select sugar can only be applied to a `<select>`");
  }
  let insertType = "math";
  if (node.attributes.type) {
    const children = node.attributes.type.children;
    if (children.length === 1 && children[0].type === "text") {
      insertType = children[0].value;
      if (!["math", "text", "number", "boolean"].includes(insertType)) {
        insertType = "math";
      }
    }
  }
  const groupResult = groupTextAndReferencesBySpacesOutsideParens({
    children: node.children,
    componentType: insertType
  });
  if (groupResult.success) {
    let newChildren = groupResult.newChildren.map(
      (child) => ({
        type: "element",
        name: "option",
        children: [child],
        attributes: {},
        source_doc: child.source_doc
      })
    );
    node.children = newChildren;
  }
}
function postponeRenderSugar(node) {
  if (!["solution", "givenAnswer", "aside", "proof"].includes(node.name)) {
    throw Error(
      "Postpone render sugar can only be applied to a `<solution>`, `<aside>`, or `<proof>`"
    );
  }
  const titleChildren = [];
  const postponedChildren = [];
  for (const child of node.children) {
    if (child.type === "element" && child.name === "title") {
      titleChildren.push(child);
    } else {
      postponedChildren.push(child);
    }
  }
  node.children = [
    ...titleChildren,
    {
      type: "element",
      name: "_postponeRenderContainer",
      children: postponedChildren,
      attributes: {},
      source_doc: node.source_doc
    }
  ];
}
function startsWithNonLetter(str) {
  return typeof str === "string" && !str.charAt(0).match(/[a-zA-Z]/);
}
function containsInvalidNameCharacters(str) {
  return typeof str === "string" && !str.match(/^[a-zA-Z0-9-_]+$/);
}
const pluginEnforceValidNames = () => {
  return (tree) => {
    visit(tree, (node, info) => {
      if (!isDastElement(node)) {
        return;
      }
      if (startsWithNonLetter(node.name) && node.name !== "_error") {
        const name = node.name;
        const dastError = codedDastError({
          code: "doenet-e0024",
          message: `Invalid component name "${name}". Names must start with a letter.`,
          args: { name },
          position: node.position
        });
        if (info.index !== void 0 && info.parents[0]) {
          info.parents[0].children.splice(info.index, 1, dastError);
        } else {
          console.warn(
            "No index found for error element, appending to root."
          );
          tree.children.push(dastError);
        }
      }
      if (node.attributes.name) {
        const name = toXml(node.attributes.name.children);
        const nonLetter = startsWithNonLetter(name);
        const invalidChar = containsInvalidNameCharacters(name);
        if (nonLetter || invalidChar) {
          const reason = nonLetter ? "start" : "characters";
          const message = `Invalid attribute name='${name}'. ${nonLetter ? "Names must start with a letter." : "Names can contain only letters, numbers, underscores or hyphens."}`;
          const dastError = codedDastError({
            code: "doenet-e0025",
            message,
            args: { name, reason },
            position: node.attributes.name?.position
          });
          delete node.attributes.name;
          if (info.index !== void 0 && info.parents[0]) {
            info.parents[0].children.splice(
              info.index + 1,
              0,
              dastError
            );
          } else {
            console.warn(
              "No index found for error element, appending to root."
            );
            tree.children.push(dastError);
          }
        }
      }
    });
  };
};
function pretzelSugar(node) {
  if (node.name !== "pretzel") {
    throw Error("Pretzel sugar can only be applied to a `<pretzel>`");
  }
  for (const child of node.children) {
    if (child.type === "element" && child.name === "problem") {
      for (const grandChild of child.children) {
        if (grandChild.type === "element" && grandChild.name === "answer") {
          grandChild.name = "givenAnswer";
        }
      }
    }
  }
  node.children = [
    {
      type: "element",
      name: "_pretzelArranger",
      children: node.children,
      attributes: node.attributes.mode ? { mode: node.attributes.mode } : {},
      source_doc: node.source_doc,
      position: node.position
    }
  ];
}
const ATTRIBUTE_NAME = "description";
const CHILD_NAME = "shortDescription";
function descriptionAttributeSugar(node) {
  if (![
    "image",
    "video",
    "graph",
    "answer",
    "mathInput",
    "textInput",
    "choiceInput",
    "booleanInput",
    "matrixInput",
    "fractionInput"
  ].includes(node.name)) {
    throw Error(
      "Description attribute sugar can only be applied to an `<image>`, `<video>`, `<graph>`, `<answer>` or `<*Input>`."
    );
  }
  const descriptionAttribute = node.attributes[ATTRIBUTE_NAME];
  if (descriptionAttribute) {
    delete node.attributes[ATTRIBUTE_NAME];
    const shortDescriptionChild = {
      type: "element",
      name: CHILD_NAME,
      children: descriptionAttribute.children,
      position: descriptionAttribute.position,
      source_doc: descriptionAttribute.source_doc,
      attributes: {}
    };
    const deprecation = codedDastError({
      code: "doenet-w0120",
      error_type: "warning",
      message: `[deprecation] Attribute \`${ATTRIBUTE_NAME}\` on \`<${node.name}>\` is deprecated; use a \`<${CHILD_NAME}>\` child instead.`,
      args: {
        attribute: ATTRIBUTE_NAME,
        child: CHILD_NAME,
        // The component the attribute was written on, since this sugar
        // covers ten of them.
        component: node.name
      },
      position: descriptionAttribute.position ?? node.position,
      source_doc: descriptionAttribute.source_doc ?? node.source_doc
    });
    node.children.unshift(deprecation, shortDescriptionChild);
  }
}
function fractionInputSugar(node) {
  const makeInput = (part) => ({
    type: "element",
    name: "_fractionInputComponent",
    attributes: {
      part: {
        type: "attribute",
        name: "part",
        children: [{ type: "text", value: part }]
      }
    },
    children: [],
    position: node.position,
    source_doc: node.source_doc
  });
  node.children.splice(
    0,
    0,
    makeInput("numerator"),
    makeInput("denominator")
  );
}
const SECTIONING_COMPONENTS_WITH_DYNAMIC_CHILDREN = [
  "section",
  "subsection",
  "subsubsection",
  "paragraphs",
  "part",
  "task",
  "aside",
  "objectives",
  "problem",
  "exercise",
  "question",
  "activity",
  "example",
  "definition",
  "note",
  "theorem",
  "proof",
  "problems",
  "exercises"
];
const COMPONENTS_WITH_DYNAMIC_CHILDREN = /* @__PURE__ */ new Set([
  "graph",
  "stickyGroup",
  ...SECTIONING_COMPONENTS_WITH_DYNAMIC_CHILDREN
]);
function effectiveComponentType(node) {
  if (node.name === "division") {
    const typeAttr = node.attributes.type;
    const child = typeAttr?.children?.[0];
    if (child && child.type === "text") {
      return child.value;
    }
  }
  return node.name;
}
function nodeSupportsDynamicChildren(node) {
  return COMPONENTS_WITH_DYNAMIC_CHILDREN.has(effectiveComponentType(node));
}
function addDynamicChildrenSugar(node) {
  const deferUntilParentRendered = node.children.some(
    (child) => child.type === "element" && child.name === "_postponeRenderContainer"
  );
  node.children.push({
    type: "element",
    name: "_dynamicChildren",
    children: [],
    attributes: deferUntilParentRendered ? {
      deferUntilParentRendered: {
        type: "attribute",
        name: "deferUntilParentRendered",
        children: [{ type: "text", value: "true" }],
        source_doc: node.source_doc
      }
    } : {},
    source_doc: node.source_doc
  });
}
function answerSugar(node) {
  if (node.name !== "answer") {
    throw Error("answer sugar can only be applied to a `<answer>`");
  }
  const typeAttr = node.attributes.type;
  if (!typeAttr) {
    return;
  }
  const typeChildren = typeAttr.children;
  if (typeChildren.length === 1 && typeChildren[0].type === "text" && typeChildren[0].value.toLowerCase() === "videowatched") {
    if (node.children.find(
      (child) => child.type === "element" && child.name === "award"
    )) {
      return;
    }
    const videoAttr = node.attributes.video;
    if (!videoAttr) {
      const dastError = codedDastError({
        code: "doenet-e0026",
        message: "Answer with type videoWatched must have a video attribute",
        position: node.position
      });
      node.children.unshift(dastError);
      return;
    }
    const videoChildren = videoAttr.children;
    if (videoChildren.length !== 1 || videoChildren[0].type !== "macro") {
      const dastError = codedDastError({
        code: "doenet-e0027",
        message: "Answer with type videoWatched must have video attribute that is a reference",
        position: videoAttr.position
      });
      node.children.unshift(dastError);
      return;
    }
    const videoRef = JSON.parse(JSON.stringify(videoChildren[0]));
    const videoFractionWatchedRef = JSON.parse(JSON.stringify(videoRef));
    videoFractionWatchedRef.path.push({
      type: "pathPart",
      name: "fractionWatched",
      index: []
    });
    let videoCreditLabel;
    const videoCreditLabelAttr = node.attributes.videoCreditLabel;
    if (videoCreditLabelAttr) {
      videoCreditLabel = {
        type: "element",
        name: "text",
        attributes: {},
        children: [
          ...videoCreditLabelAttr.children,
          { type: "text", value: ": " }
        ],
        position: videoCreditLabelAttr.position,
        source_doc: videoCreditLabelAttr.source_doc
      };
    } else {
      videoCreditLabel = {
        type: "element",
        name: "text",
        attributes: {},
        children: [
          {
            type: "text",
            value: "Video credit achieved: "
          }
        ]
      };
    }
    let answerNameAttr = node.attributes.name;
    if (!answerNameAttr) {
      const ansName = `__answer${node.position?.start.offset || "unknown"}`;
      answerNameAttr = node.attributes.name = {
        type: "attribute",
        name: "name",
        children: [
          {
            type: "text",
            value: ansName
          }
        ]
      };
    }
    if (answerNameAttr.children.length !== 1 || answerNameAttr.children[0].type !== "text") {
      const dastError = codedDastError({
        code: "doenet-e0028",
        message: "Answer name attribute must have a single text child",
        position: answerNameAttr.position
      });
      node.children.unshift(dastError);
      return;
    }
    node.attributes.hide = {
      type: "attribute",
      name: "hide",
      children: [
        {
          type: "text",
          value: "true"
        }
      ]
    };
    const answerName = answerNameAttr.children[0].value;
    node.children.push({
      type: "element",
      name: "award",
      attributes: {
        credit: {
          type: "attribute",
          name: "credit",
          children: [
            JSON.parse(JSON.stringify(videoFractionWatchedRef))
          ]
        }
      },
      children: [
        {
          type: "element",
          name: "when",
          attributes: {},
          children: [{ type: "text", value: "true" }]
        }
      ]
    });
    const videoCreditMessage = {
      type: "element",
      name: "span",
      attributes: {},
      children: [
        videoCreditLabel,
        {
          type: "element",
          name: "number",
          attributes: {
            displayDecimals: {
              type: "attribute",
              name: "displayDecimals",
              children: [
                {
                  type: "text",
                  value: "1"
                }
              ]
            }
          },
          children: [
            videoFractionWatchedRef,
            { type: "text", value: "*100" }
          ]
        },
        {
          type: "text",
          value: "%"
        }
      ]
    };
    const callActionElement = {
      type: "element",
      name: "callAction",
      children: [],
      attributes: {
        actionName: {
          type: "attribute",
          name: "actionName",
          children: [
            {
              type: "text",
              value: `submitAnswer`
            }
          ]
        },
        triggerWith: {
          type: "attribute",
          name: "triggerWith",
          children: [videoRef]
        },
        target: {
          type: "attribute",
          name: "target",
          children: [
            {
              type: "macro",
              path: [
                {
                  type: "pathPart",
                  name: answerName,
                  index: []
                }
              ],
              attributes: {}
            }
          ]
        },
        hide: {
          type: "attribute",
          name: "hide",
          children: [
            {
              type: "text",
              value: "true"
            }
          ]
        }
      }
    };
    const answerElement = {
      type: "element",
      name: "answer",
      attributes: node.attributes,
      children: node.children,
      position: node.position,
      source_doc: node.source_doc
    };
    node.name = "span";
    node.attributes = {};
    node.children = [answerElement, videoCreditMessage, callActionElement];
  }
}
const DEFAULT_VARIABLES = "x y";
const NON_FUNCTION_CHILDREN = /* @__PURE__ */ new Set([
  "label",
  "xLabel",
  "yLabel",
  "description",
  "shortDescription"
]);
function fieldFunctionSugar(node) {
  if (!["slopeField", "vectorField"].includes(node.name)) {
    throw Error(
      "Field function sugar can only be applied to a `<slopeField>` or `<vectorField>`."
    );
  }
  const isLeftAlone = (child) => child.type === "element" && NON_FUNCTION_CHILDREN.has(child.name);
  const isBlankText = (child) => child.type === "text" && child.value.trim() === "";
  const toWrap = node.children.filter((child) => !isLeftAlone(child));
  const substantive = toWrap.filter((child) => !isBlankText(child));
  if (substantive.length === 0) {
    warnVariablesIgnored(node, "no-expression");
    return;
  }
  if (substantive.every(
    (child) => child.type === "element" && child.name === "function"
  )) {
    warnVariablesIgnored(node, "function-child");
    return;
  }
  const writtenKey = variablesAttributeKey(node);
  const written = writtenKey ? node.attributes[writtenKey] : void 0;
  const variables = written ? { ...written, name: "variables" } : {
    type: "attribute",
    name: "variables",
    children: [{ type: "text", value: DEFAULT_VARIABLES }],
    position: node.position,
    source_doc: node.source_doc
  };
  if (writtenKey) {
    delete node.attributes[writtenKey];
  }
  const functionChild = {
    type: "element",
    name: "function",
    attributes: { variables },
    children: toWrap,
    position: node.position,
    source_doc: node.source_doc
  };
  const firstExpressionIndex = node.children.indexOf(substantive[0]);
  node.children = node.children.flatMap((child, index) => {
    if (isLeftAlone(child)) {
      return [child];
    }
    return index === firstExpressionIndex ? [functionChild] : [];
  });
}
function variablesAttributeKey(node) {
  return Object.keys(node.attributes).find(
    (name) => name.toLowerCase() === "variables"
  );
}
function warnVariablesIgnored(node, reason) {
  const key = variablesAttributeKey(node);
  if (!key) {
    return;
  }
  const variables = node.attributes[key];
  const explanation = reason === "function-child" ? "The function here is given as a `<function>` child, which names its own variables, so `variables` is ignored." : "No such expression is given here, so `variables` is ignored.";
  const warning = codedDastError({
    code: "doenet-w0124",
    error_type: "warning",
    message: `\`<${node.name}>\`: the \`variables\` attribute names the variables of an expression written directly inside the component. ${explanation}`,
    args: {
      // The field's own tag, since this sugar covers both of them.
      component: node.name,
      reason
    },
    position: variables.position ?? node.position,
    source_doc: variables.source_doc ?? node.source_doc
  });
  node.children.unshift(warning);
}
const SCORED_SECTION_COMPONENT_TYPES = [
  "document",
  "section",
  "subsection",
  "subsubsection",
  "paragraphs",
  "aside",
  "objectives",
  "problem",
  "exercise",
  "question",
  "activity",
  "example",
  "definition",
  "note",
  "theorem",
  "part",
  "task",
  "proof",
  "problems",
  "exercises",
  "standinForFutureLayoutTag",
  "externalContent",
  "cascade",
  "div",
  "span",
  "ol",
  "ul",
  "li",
  "p"
];
function renamedScoredSectionAttribute(oldName, newName) {
  const rule = { from: oldName, to: newName };
  return Object.fromEntries(
    SCORED_SECTION_COMPONENT_TYPES.map((comp) => [
      comp,
      { [oldName]: rule }
    ])
  );
}
function ignoredAttributes(componentName, attributeNames) {
  return Object.fromEntries(
    attributeNames.map((attributeName) => [
      attributeName,
      { attribute: attributeName, component: componentName }
    ])
  );
}
const INPUT_COMPONENT_TYPES = [
  "booleanInput",
  "choiceInput",
  "fractionInput",
  "mathInput",
  "matrixInput",
  "textInput"
];
const TABULAR_HALIGN_COMPONENT_TYPES = ["cell", "row", "tabular"];
const TABULAR_BORDER_RENAMES = {
  cell: ["right", "bottom"],
  row: ["left", "bottom"],
  tabular: ["top", "left", "bottom", "right"]
};
const BORDER_ATTRIBUTE_REPLACEMENTS = {
  left: "startBorder",
  right: "endBorder",
  top: "topBorder",
  bottom: "bottomBorder"
};
function tabularBorderRenames() {
  return Object.fromEntries(
    Object.entries(TABULAR_BORDER_RENAMES).map(([component, edges]) => [
      component,
      Object.fromEntries(
        edges.map((edge) => [
          edge,
          {
            from: edge,
            to: BORDER_ATTRIBUTE_REPLACEMENTS[edge],
            component
          }
        ])
      )
    ])
  );
}
function renamedAttributeValues(component, attribute, mapping) {
  return {
    [attribute]: Object.fromEntries(
      Object.entries(mapping).map(([from, to]) => [
        from,
        { attribute, from, to, component }
      ])
    )
  };
}
function renamedAttributeValuesAcross(components, attribute, mapping) {
  return Object.fromEntries(
    components.map((component) => [
      component,
      renamedAttributeValues(component, attribute, mapping)
    ])
  );
}
const SCORED_SECTION_COLORING_RENAMES = renamedScoredSectionAttribute(
  "forceIndividualAnswerColoring",
  "colorAnswersSeparately"
);
const TABULAR_BORDER_ATTRIBUTE_RENAMES = tabularBorderRenames();
const DEPRECATION_REGISTRY = {
  attributeRenames: {
    // Merge per-component rules, spreading scored-section rename rules
    // first so component-specific rules take precedence on conflict.
    ...SCORED_SECTION_COLORING_RENAMES,
    ...TABULAR_BORDER_ATTRIBUTE_RENAMES,
    document: {
      // document already has a scored-section rename (spread above) plus
      // its own documentWideCheckWork rename.
      ...SCORED_SECTION_COLORING_RENAMES["document"],
      documentWideCheckWork: {
        from: "documentWideCheckWork",
        to: "sectionWideCheckWork",
        component: "document"
      }
    },
    selectFromSequence: {
      sortResults: {
        from: "sortResults",
        to: "sort",
        component: "selectFromSequence"
      }
    },
    selectPrimeNumbers: {
      minValue: {
        from: "minValue",
        to: "from",
        component: "selectPrimeNumbers"
      },
      maxValue: {
        from: "maxValue",
        to: "to",
        component: "selectPrimeNumbers"
      },
      sortResults: {
        from: "sortResults",
        to: "sort",
        component: "selectPrimeNumbers"
      }
    },
    samplePrimeNumbers: {
      minValue: {
        from: "minValue",
        to: "from",
        component: "samplePrimeNumbers"
      },
      maxValue: {
        from: "maxValue",
        to: "to",
        component: "samplePrimeNumbers"
      }
    }
  },
  attributeRemovals: {
    // `description` and `shortDescription` previously inherited these
    // attributes from `Div` / `Text`, which are inappropriate parents for
    // them. The attributes never had a meaningful effect on these
    // components; they are dropped with a warning.
    description: ignoredAttributes("description", [
      "aggregateScores",
      "weight",
      "sectionWideCheckWork",
      "showCorrectness",
      "colorCorrectness",
      "forceIndividualAnswerColoring",
      "submitLabel",
      "submitLabelNoCorrectness",
      "displayDigitsForCreditAchieved"
    ]),
    shortDescription: ignoredAttributes("shortDescription", [
      "draggable",
      "layer",
      "isLatex",
      "anchor",
      "positionFromAnchor"
    ]),
    // `align` was the only mode an ODE system could ever be rendered in:
    // its LaTeX carries `&` alignment markers and its own `\tag`/`\notag`
    // (equation numbering is controlled by `number`), none of which are
    // legal in the delimiters the other modes of the math renderer supply.
    // The mode is now fixed by the component, so the attribute is dropped.
    odeSystem: ignoredAttributes("odeSystem", ["renderMode"])
  },
  attributeValueRenames: {
    // The label sits beside the input in DOM order, which mirrors with the
    // writing direction, so `left`/`right` name the side wrongly in a
    // right-to-left document.
    ...renamedAttributeValuesAcross(
      INPUT_COMPONENT_TYPES,
      "labelPosition",
      { left: "start", right: "end" }
    ),
    // The results panel and the editor are placed in DOM order and mirror
    // with the writing direction, the way panes conventionally do.
    codeEditor: renamedAttributeValues("codeEditor", "resultsLocation", {
      left: "start",
      right: "end"
    }),
    // `halign` becomes `text-align`, whose `left`/`right` do not mirror
    // while everything around them does.
    ...renamedAttributeValuesAcross(
      TABULAR_HALIGN_COMPONENT_TYPES,
      "halign",
      { left: "start", right: "end" }
    )
  }
};
function lowerKeys(o) {
  return Object.fromEntries(
    Object.entries(o).map(([k2, v]) => [k2.toLowerCase(), v])
  );
}
function buildDeprecationIndex(registry) {
  return {
    attributeRenames: Object.fromEntries(
      Object.entries(registry.attributeRenames).map(([comp, rules]) => [
        comp,
        lowerKeys(rules)
      ])
    ),
    attributeRemovals: Object.fromEntries(
      Object.entries(registry.attributeRemovals).map(([comp, rules]) => [
        comp,
        lowerKeys(rules)
      ])
    ),
    // Attribute values are matched case-insensitively too, so
    // `labelPosition="Left"` is as much the deprecated value as `left` is.
    attributeValueRenames: Object.fromEntries(
      Object.entries(registry.attributeValueRenames).map(
        ([comp, attrRules]) => [
          comp,
          lowerKeys(
            Object.fromEntries(
              Object.entries(attrRules).map(([attr, rules]) => [
                attr,
                lowerKeys(rules)
              ])
            )
          )
        ]
      )
    )
  };
}
const DEPRECATION_INDEX = buildDeprecationIndex(DEPRECATION_REGISTRY);
function findAttributeKey(node, lowerName) {
  return Object.keys(node.attributes).find(
    (k2) => k2.toLowerCase() === lowerName
  );
}
const pluginApplyDeprecations = () => {
  return (tree) => {
    const warnings = [];
    visit(tree, (node) => {
      if (!isDastElement(node)) {
        return;
      }
      applyAttributeRenames(node, warnings);
      applyAttributeRemovals(node, warnings);
      applyAttributeValueRenames(node, warnings);
    });
    if (warnings.length > 0) {
      const documentElement = tree.children.find(
        (child) => isDastElement(child) && child.name === "document"
      );
      if (documentElement) {
        documentElement.children.push(...warnings);
      }
    }
  };
};
function applyAttributeRenames(node, warnings) {
  const renameRules = DEPRECATION_INDEX.attributeRenames[node.name];
  if (!renameRules) {
    return;
  }
  for (const [oldLowerName, renameRule] of Object.entries(renameRules)) {
    const oldActualKey = findAttributeKey(node, oldLowerName);
    if (!oldActualKey) {
      continue;
    }
    const oldAttribute = node.attributes[oldActualKey];
    const position = oldAttribute.position ?? node.position;
    const source_doc = oldAttribute.source_doc ?? node.source_doc;
    delete node.attributes[oldActualKey];
    const conflictKey = findAttributeKey(node, renameRule.to.toLowerCase());
    if (conflictKey) {
      warnings.push(
        renamedAttributeConflictWarning(
          renameRule,
          position,
          source_doc
        )
      );
    } else {
      node.attributes[renameRule.to] = {
        ...oldAttribute,
        name: renameRule.to
      };
      warnings.push(
        renamedAttributeWarning(renameRule, position, source_doc)
      );
    }
  }
}
function applyAttributeRemovals(node, warnings) {
  const removalRules = DEPRECATION_INDEX.attributeRemovals[node.name];
  if (!removalRules) {
    return;
  }
  for (const [lowerName, removalRule] of Object.entries(removalRules)) {
    const actualKey = findAttributeKey(node, lowerName);
    if (!actualKey) {
      continue;
    }
    const attribute = node.attributes[actualKey];
    const position = attribute.position ?? node.position;
    const source_doc = attribute.source_doc ?? node.source_doc;
    delete node.attributes[actualKey];
    warnings.push(
      deprecationWarning({
        code: "doenet-w0110",
        message: `[deprecation] Attribute \`${removalRule.attribute}\` on \`<${removalRule.component}>\` is deprecated and ignored.`,
        args: {
          attribute: removalRule.attribute,
          component: removalRule.component
        },
        position,
        source_doc
      })
    );
  }
}
function applyAttributeValueRenames(node, warnings) {
  const attributeRules = DEPRECATION_INDEX.attributeValueRenames[node.name];
  if (!attributeRules) {
    return;
  }
  for (const [lowerAttrName, valueRules] of Object.entries(attributeRules)) {
    const actualKey = findAttributeKey(node, lowerAttrName);
    if (!actualKey) {
      continue;
    }
    const attribute = node.attributes[actualKey];
    const [child, ...rest] = attribute.children;
    if (rest.length > 0 || child?.type !== "text") {
      continue;
    }
    const rule = valueRules[child.value.trim().toLowerCase()];
    if (!rule) {
      continue;
    }
    child.value = rule.to;
    warnings.push(
      deprecationWarning({
        code: "doenet-w0121",
        message: `[deprecation] Value \`${rule.from}\` of attribute \`${rule.attribute}\` on \`<${rule.component}>\` is deprecated; use \`${rule.to}\` instead.`,
        args: {
          value: rule.from,
          attribute: rule.attribute,
          component: rule.component,
          to: rule.to
        },
        position: attribute.position ?? node.position,
        source_doc: attribute.source_doc ?? node.source_doc
      })
    );
  }
}
function onComponent(component) {
  return component === void 0 ? "" : ` on \`<${component}>\``;
}
function renameArgs(rule) {
  return {
    from: rule.from,
    to: rule.to,
    component: rule.component ?? "none"
  };
}
function renamedAttributeWarning(rule, position, source_doc) {
  return deprecationWarning({
    code: "doenet-w0108",
    message: `[deprecation] Attribute \`${rule.from}\`${onComponent(rule.component)} is deprecated; use \`${rule.to}\` instead.`,
    args: renameArgs(rule),
    position,
    source_doc
  });
}
function renamedAttributeConflictWarning(rule, position, source_doc) {
  return deprecationWarning({
    code: "doenet-w0109",
    message: `[deprecation] Attribute \`${rule.from}\`${onComponent(rule.component)} is deprecated and ignored because \`${rule.to}\` is also specified.`,
    args: renameArgs(rule),
    position,
    source_doc
  });
}
function deprecationWarning(diagnostic) {
  return codedDastError({ ...diagnostic, error_type: "warning" });
}
function normalizeDocumentDast(dast, addCompatibilityNames = false) {
  let processor = unified().use(pluginRemoveCommentsInstructionsAndDocStrings).use(pluginChangeCdataToText).use(pluginEnsureDocumentElement).use(pluginConvertPretextAttributes).use(pluginEnforceValidNames).use(pluginExpandAliasedElements).use(pluginApplyDeprecations);
  if (addCompatibilityNames) {
    processor = processor.use(pluginAddCompatibilityNames);
  }
  processor = processor.use(pluginComponentSugar);
  return processor.runSync(dast);
}
const pluginChangeCdataToText = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (node.type === "cdata") {
        node.type = "text";
      }
    });
  };
};
const pluginRemoveCommentsInstructionsAndDocStrings = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (node.type === "element" || node.type === "root") {
        node.children = node.children.filter(
          (n) => n.type !== "comment" && n.type !== "instruction" && n.type !== "doctype"
        );
      }
    });
  };
};
const pluginEnsureDocumentElement = () => {
  return (tree) => {
    const existingDocument = tree.children.find(
      (n) => n.type === "element" && n.name === "document"
    );
    if (existingDocument) {
      existingDocument.children = trimWhitespaceNodes(
        existingDocument.children
      );
      if (tree.children.length > 1) {
        tree.children = [existingDocument];
      }
    } else {
      const children = trimWhitespaceNodes(
        // This filtering may be redundant, but it should also be very fast.
        filterNonDoenetXml(tree.children)
      );
      tree.children = [
        {
          type: "element",
          name: "document",
          attributes: {},
          children
        }
      ];
    }
  };
};
const pluginExpandAliasedElements = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (isDastElement(node)) {
        const expansion = ELEMENT_EXPANSIONS[node.name];
        if (expansion) {
          let newAttributes = Object.fromEntries(
            Object.entries(expansion.attributes || {}).map(
              ([key, value]) => {
                let newAttr = {
                  type: "attribute",
                  name: key,
                  children: [{ type: "text", value }]
                };
                return [key, newAttr];
              }
            )
          );
          node.name = expansion.to;
          node.attributes = {
            ...node.attributes,
            ...newAttributes
          };
        }
      }
    });
  };
};
const pluginAddCompatibilityNames = () => {
  const counts = {};
  return (tree) => {
    visit(tree, (node) => {
      if (isDastElement(node)) {
        const typeCount = counts[node.name] = (counts[node.name] ?? 0) + 1;
        if (!node.attributes.name) {
          const name = `_${node.name}${typeCount}`;
          node.attributes.name = {
            type: "attribute",
            name: "name",
            children: [{ type: "text", value: name }]
          };
        }
      }
    });
  };
};
const pluginComponentSugar = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (!isDastElement(node)) {
        return;
      }
      switch (node.name) {
        case "repeat":
        case "repeatForSequence":
          repeatSugar(node);
          break;
        case "conditionalContent":
          conditionalContentSugar(node);
          break;
        case "select":
          selectSugar(node);
          break;
        case "solution":
        case "givenAnswer":
          postponeRenderSugar(node);
          break;
        case "aside":
        case "proof":
          if (node.attributes.postponeRendering) {
            const children = node.attributes.postponeRendering.children;
            if (children.length === 0 || children.length === 1 && children[0].type === "text" && children[0].value.toLowerCase() === "true") {
              postponeRenderSugar(node);
            }
          }
          break;
        case "pretzel":
          pretzelSugar(node);
          break;
        case "graph":
          descriptionAttributeSugar(node);
          break;
        case "answer":
          descriptionAttributeSugar(node);
          answerSugar(node);
          break;
        case "image":
        case "video":
        case "mathInput":
        case "textInput":
        case "booleanInput":
        case "choiceInput":
        case "matrixInput":
          descriptionAttributeSugar(node);
          break;
        case "fractionInput":
          descriptionAttributeSugar(node);
          fractionInputSugar(node);
          break;
        case "slopeField":
        case "vectorField":
          fieldFunctionSugar(node);
          break;
      }
      if (nodeSupportsDynamicChildren(node)) {
        addDynamicChildrenSugar(node);
      }
    });
  };
};
function filterNonDoenetXml(nodes) {
  return nodes.filter(
    (n) => n.type !== "doctype" && n.type !== "instruction"
  );
}
function trimWhitespaceNodes(nodes) {
  if (nodes.length === 0) {
    return nodes;
  }
  const firstNonWhitespaceIdx = nodes.findIndex(
    (n) => n.type !== "text" || /\S/.test(n.value)
  );
  if (firstNonWhitespaceIdx === -1) {
    return [];
  }
  const lastNonWhitespaceIdx = findLastIndex(
    nodes,
    (n) => n.type !== "text" || /\S/.test(n.value)
  );
  const trimmed = nodes.slice(
    firstNonWhitespaceIdx,
    lastNonWhitespaceIdx + 1
  );
  const first = trimmed[0];
  const last = trimmed[trimmed.length - 1];
  if (first?.type === "text" && /^\s/.test(first.value)) {
    trimmed[0] = {
      ...first,
      value: first.value.trimStart()
    };
  }
  if (last?.type === "text" && /\s$/.test(last.value)) {
    trimmed[trimmed.length - 1] = {
      ...last,
      value: last.value.trimEnd()
    };
  }
  return trimmed;
}
function findLastIndex(arr, predicate) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (predicate(arr[i])) {
      return i;
    }
  }
  return -1;
}
async function expandExternalReferences(dast, fetchExternalDoenetML) {
  const pluginSubstituteExternalReferences = () => {
    return async (tree) => {
      const promiseStack = [];
      let maxRecursion = 10;
      let counter = 0;
      const findExternalReferenceAttribute = (node) => {
        if (!isDastElement(node)) {
          return;
        }
        const sourceAttributeName = Object.keys(node.attributes).find(
          (attrName) => ["extend", "copy"].includes(attrName.toLowerCase())
        );
        if (sourceAttributeName === void 0) {
          return;
        }
        const sourceAttribute = node.attributes[sourceAttributeName];
        const sourceUri = toXml(sourceAttribute.children);
        if (!sourceUri.startsWith("doenet:")) {
          return;
        }
        promiseStack.push({
          sourceUri,
          source: fetchExternalDoenetML(sourceUri),
          parent: node,
          sourceAttribute,
          recursionLevel: counter
        });
      };
      visit(tree, findExternalReferenceAttribute);
      let unresolved;
      while (unresolved = promiseStack.pop()) {
        const parent = unresolved.parent;
        if (unresolved.recursionLevel > maxRecursion) {
          const dastError = codedDastError({
            code: "doenet-e0029",
            message: `Unable to retrieve external DoenetML due to too many levels of recursion. Is there a circular reference?`,
            position: unresolved.sourceAttribute.position
          });
          parent.children.unshift(dastError);
          continue;
        }
        let externalDoenetML;
        try {
          externalDoenetML = await unresolved.source;
        } catch (e) {
          console.error(e);
          const dastError = codedDastError({
            code: "doenet-e0030",
            message: `Unable to retrieve DoenetML from ${unresolved.sourceAttribute.name}="${unresolved.sourceUri}"`,
            args: {
              attribute: unresolved.sourceAttribute.name,
              uri: unresolved.sourceUri
            },
            position: unresolved.sourceAttribute.position
          });
          parent.children.unshift(dastError);
          continue;
        }
        const externalDast = lezerToDast(externalDoenetML);
        const matchResult = findMatchingChild(
          externalDast,
          parent.name
        );
        if (!matchResult.success) {
          const dastError = codedDastError({
            code: "doenet-e0031",
            message: `Invalid DoenetML retrieved from ${unresolved.sourceAttribute.name}="${unresolved.sourceUri}": it did not match the component type "${parent.name}"`,
            args: {
              attribute: unresolved.sourceAttribute.name,
              uri: unresolved.sourceUri,
              componentType: parent.name
            },
            position: unresolved.sourceAttribute.position
          });
          parent.children.unshift(dastError);
          continue;
        }
        const child = matchResult.child;
        if (!dast.sources) {
          dast.sources = [""];
        }
        const source_doc = dast.sources.length;
        dast.sources.push(externalDoenetML);
        for (const attrName in parent.attributes) {
          if (["extend", "copy"].includes(attrName.toLowerCase())) {
            delete parent.attributes[attrName];
          }
        }
        counter = unresolved.recursionLevel + 1;
        mergeExternalChildIntoParent(
          parent,
          child,
          source_doc,
          findExternalReferenceAttribute
        );
      }
    };
  };
  let processor = unified().use(pluginSubstituteExternalReferences);
  return await processor.run(dast);
}
function mergeExternalChildIntoParent(parent, child, source_doc, findExternalReferenceAttribute) {
  const addDocSource = (node) => {
    node.source_doc = source_doc;
    switch (node.type) {
      case "element":
        for (const attrName in node.attributes) {
          const attribute = node.attributes[attrName];
          attribute.source_doc = source_doc;
          for (const attrChild of attribute.children) {
            visit(attrChild, addDocSource);
          }
        }
        break;
      case "macro":
      case "function":
        for (const path of node.path) {
          path.source_doc = source_doc;
          for (const index of path.index) {
            index.source_doc = source_doc;
            for (const value of index.value) {
              visit(value, addDocSource);
            }
          }
        }
        break;
    }
  };
  visit(child, addDocSource);
  const parentAttributes = { ...child.attributes };
  if (parentAttributes.name) {
    const annotated_name = `source-${source_doc}:name`;
    parentAttributes[annotated_name] = parentAttributes.name;
    parentAttributes[annotated_name].name = annotated_name;
    delete parentAttributes.name;
  }
  Object.assign(parentAttributes, parent.attributes);
  parent.attributes = parentAttributes;
  if (!parent.attributes[`source:sequence`]) {
    parent.attributes[`source:sequence`] = {
      type: "attribute",
      name: "source:sequence",
      children: [
        {
          type: "text",
          value: parent.source_doc?.toString() ?? "0"
        }
      ]
    };
  }
  parent.attributes["source:sequence"].children.push({
    type: "text",
    value: source_doc.toString()
  });
  findExternalReferenceAttribute(parent);
  for (const newChild of child.children) {
    visit(newChild, findExternalReferenceAttribute);
  }
  parent.children.unshift(...child.children);
}
function findMatchingChild(dastRoot, elementName) {
  const rootChildren = dastRoot.children.filter(
    (node) => node.type !== "text" || node.value.trim() !== ""
  );
  if (rootChildren.length === 1 && isDastElement(rootChildren[0])) {
    let child = rootChildren[0];
    if (child.name === elementName) {
      return { success: true, child };
    }
    if (child.name === "document") {
      const documentChildren = child.children.filter(
        (node) => node.type !== "text" || node.value.trim() !== ""
      );
      if (documentChildren.length === 1 && isDastElement(documentChildren[0])) {
        child = documentChildren[0];
        if (child.name === elementName) {
          return { success: true, child };
        }
      }
    }
  }
  return { success: false };
}
export {
  C as CONTINUE,
  E as EXIT,
  S as SKIP,
  codedDastError,
  declaredLangsInSource,
  detectVersionFromDoenetML,
  expandExternalReferences,
  extractDastErrors,
  k as filterPositionInfo,
  isDastElement,
  isDastElementContent,
  lezerToDast,
  normalizeDocumentDast,
  parse,
  parser,
  readDeclaredLangs,
  readLangAttribute,
  r as replaceNode,
  showCursor,
  showNode,
  s as stringToLezer,
  toXml,
  a as unquotedAttributeValueMessage,
  visit
};
//# sourceMappingURL=index.js.map
